/**
 * Tiny GA4 event helper. SiteMetadataManager loads gtag.js + the base
 * config; everything else fires through trackEvent so we keep the call sites
 * uniform and safe when gtag never loads (localhost, ad-blockers, etc).
 */

import { sanitizeUrlForAnalytics } from "@/lib/urlPrivacy";

type GtagParams = Record<string, unknown>;

const URL_PARAMETER_NAMES = new Set([
  "page_location",
  "page_path",
  "page_referrer",
  "referrer",
  "document_location",
  "landing_url",
  "source_url",
  "link_url",
]);

export function sanitizeGtagParams(params: GtagParams): GtagParams {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      typeof value === "string" && URL_PARAMETER_NAMES.has(key.toLowerCase())
        ? sanitizeUrlForAnalytics(value)
        : value,
    ]),
  );
}

const isLocalhost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/**
 * `SiteMetadataManager` installs `window.gtag` (a `dataLayer.push` stub) on
 * mount, but events that can fire before that effect has run — or right
 * before a client-side `navigate()` tears down the page that would send the
 * hit — were landing nowhere (e.g. `sign_up`: 0 recorded in GA4 vs 7 real
 * signups). This guarantees the stub exists (queueing into `dataLayer` the
 * same way the real gtag.js loader would) and defaults every event to
 * `transport_type: "beacon"` so the hit survives a same-tick redirect.
 */
function ensureGtagStub(): void {
  if (typeof window === "undefined") return;
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  if (!window.gtag) {
    window.gtag = gtagStub;
  }
}

/**
 * The gtag stub MUST push the `arguments` object, never a rest-param array.
 * gtag.js only executes dataLayer entries that are Arguments objects; a plain
 * array is silently skipped. With the array version the `config` command never
 * ran, so this app sent zero hits to GA4 and Google Ads: no page_view on
 * /signup, /welcome/*, /live-agent/*, and no sign_up conversion (2026-09-27:
 * 0 /signup page views in 14 days while the landing pages sent 237 cta_clicks).
 */
export function gtagStub(..._args: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments);
}

function sendGtagEvent(eventName: string, params: GtagParams): void {
  ensureGtagStub();
  window.gtag?.("event", eventName, {
    transport_type: "beacon",
    ...sanitizeGtagParams(params),
  });
}

export function trackEvent(eventName: string, params: GtagParams = {}): void {
  if (typeof window === "undefined" || isLocalhost()) return;
  sendGtagEvent(eventName, params);
}

/** Fires on a `tel:` link click — the phone-side counterpart to `demo_start`. */
export function trackCallClick(context: string, params: GtagParams = {}): void {
  trackEvent("call_click", { context, ...params });
}

/** Tracks a one-shot per-session event keyed in sessionStorage so re-mounts don't double-fire. */
export function trackEventOnce(
  key: string,
  eventName: string,
  params: GtagParams = {},
): void {
  if (typeof window === "undefined" || isLocalhost()) return;
  try {
    if (window.sessionStorage.getItem(`hg_ga4_${key}`)) return;
    window.sessionStorage.setItem(`hg_ga4_${key}`, "1");
  } catch {
    // sessionStorage may be unavailable in some embed contexts; fall through and just fire.
  }
  sendGtagEvent(eventName, params);
}
